import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, BrainCircuit, AudioLines, ScanEye, TrendingUp, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const AI_IMG =
    "https://images.unsplash.com/photo-1667372283496-893f0b1e7c16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwY2xvdWQlMjBkYXRhJTIwaW5mcmFzdHJ1Y3R1cmUlMjB0ZWNobm9sb2d5JTIwZXhlY3V0aXZlfGVufDB8fHx8MTc5MDI2NjIzN3ww&ixlib=rb-4.1.0&q=85";

const TABS = [
    {
        id: "agentic",
        icon: Bot,
        label: "Agentic AI",
        title: "Agentic AI Workflows",
        body: "Autonomous agents that plan, reason and execute multi-step business processes, from document intelligence to IT operations, with human-in-the-loop guardrails.",
        points: ["Multi-agent orchestration frameworks", "Tool-use & retrieval-augmented pipelines", "Governed autonomy with audit trails"],
    },
    {
        id: "llm",
        icon: BrainCircuit,
        label: "LLM Fine-Tuning",
        title: "Domain-Tuned Language Models",
        body: "We fine-tune and distill large language models on your private data, delivering accurate, compliant assistants that speak your business language.",
        points: ["Private, on-VPC model deployment", "RLHF & evaluation harnesses", "Cost-optimized inference at scale"],
    },
    {
        id: "speech",
        icon: AudioLines,
        label: "Speech Intelligence",
        title: "Real-Time Speech & Audio",
        body: "High-precision multilingual speech-to-text and voice analytics, English, Hindi, Kannada and more, powering contact centers and field operations.",
        points: ["Real-time streaming transcription", "Speaker diarization & sentiment", "Edge and cloud deployment options"],
    },
    {
        id: "vision",
        icon: ScanEye,
        label: "Computer Vision",
        title: "Computer Vision Systems",
        body: "From quality inspection on the factory floor to intelligent surveillance analytics, our vision pipelines turn video into decisions.",
        points: ["Defect detection & visual QA", "Safety & compliance monitoring", "Custom model training on your imagery"],
    },
    {
        id: "predictive",
        icon: TrendingUp,
        label: "Predictive Analytics",
        title: "Predictive Analytics",
        body: "Forecast demand, detect anomalies and anticipate failures before they happen, built on streaming data platforms that scale with you.",
        points: ["Time-series forecasting at scale", "Real-time anomaly detection", "Decision dashboards & alerting"],
    },
];

export const AICapabilities = () => {
    const [active, setActive] = useState(TABS[0].id);
    const tab = TABS.find((t) => t.id === active);

    return (
        <section id="ai" data-testid="ai-section" className="relative border-y border-white/[0.06] bg-panel/40 py-24 sm:py-32">
            <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-volt/5 blur-[120px]" />
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="max-w-3xl">
                    <Eyebrow>Deep Rooted AI Capabilities</Eyebrow>
                    <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="ai-heading">
                        Empowering businesses with innovation, integration, and AI-driven excellence
                    </h2>
                    <p className="text-base leading-relaxed text-slate-400 sm:text-lg">
                        With a foundation deeply rooted in advanced AI capabilities, we leverage
                        cutting-edge technologies to drive innovation, optimize processes, and
                        deliver transformative solutions.
                    </p>
                </Reveal>

                <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
                    <Reveal className="relative hidden lg:block">
                        <div className="sticky top-28 overflow-hidden rounded-2xl border border-white/10">
                            <img
                                src={AI_IMG}
                                alt="Syntera AI and cloud security visualization"
                                className="h-[480px] w-full object-cover"
                                data-testid="ai-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/20 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/10 bg-[#0D111A]/85 p-5 backdrop-blur-xl">
                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt">
                                    Syntera AI CoE, Bengaluru
                                </div>
                                <div className="mt-1.5 font-syne text-lg font-bold text-white">
                                    Research-to-production in weeks, not quarters.
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <div>
                        <Reveal delay={0.1}>
                            <div className="flex flex-wrap gap-2" role="tablist" data-testid="ai-tabs">
                                {TABS.map((t) => (
                                    <button
                                        key={t.id}
                                        role="tab"
                                        aria-selected={active === t.id}
                                        data-testid={`ai-tab-${t.id}`}
                                        onClick={() => setActive(t.id)}
                                        className={`flex items-center gap-2 rounded-full border px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                                            active === t.id
                                                ? "border-volt/60 bg-volt/10 text-volt shadow-[0_0_20px_rgba(0,240,255,0.15)]"
                                                : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                                        }`}
                                    >
                                        <t.icon size={14} />
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </Reveal>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={tab.id}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="mt-8 rounded-2xl border border-white/[0.08] bg-card2/50 p-8"
                                data-testid="ai-tab-panel"
                            >
                                <h3 className="mb-3 font-syne text-xl font-semibold text-white sm:text-2xl">{tab.title}</h3>
                                <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{tab.body}</p>
                                <ul className="mt-6 space-y-3">
                                    {tab.points.map((p) => (
                                        <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-volt/15">
                                                <Check size={12} className="text-volt" />
                                            </span>
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
